import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPut, unwrap } from '@/shared/api'
import type { FocusStats, Soundscape, SoundscapePosition } from '../model/schema'
import {
  type FocusStatsDto,
  type SoundscapeDto,
  type SoundscapePositionDto,
  toFocusStats,
  toSoundscape,
  toSoundscapePosition,
} from './mappers'

export const soundscapeKeys = {
  all: ['soundscapes'] as const,
  list: () => [...soundscapeKeys.all, 'list'] as const,
  // stats() — префикс всех сводок (по нему инвалидируются и своя, и чужие),
  // statsOf(id) — конкретный сотрудник; 'me' — тот, кто смотрит.
  stats: () => ['focus', 'stats'] as const,
  statsOf: (employeeId: string) => [...soundscapeKeys.stats(), employeeId] as const,
  positions: () => ['focus', 'positions'] as const,
}

// Каталог режимов Endel. Живёт данными на бэке: новый режим появляется без релиза,
// поэтому список не кэшируем навсегда, но и дёргать его часто незачем.
export const soundscapesQueryOptions = queryOptions({
  queryKey: soundscapeKeys.list(),
  queryFn: async (): Promise<Soundscape[]> => {
    const list = unwrap<SoundscapeDto[]>(await apiGet('/focus/soundscapes')) ?? []
    return list.map(toSoundscape)
  },
  staleTime: 10 * 60 * 1000,
})

// Сколько сотрудник был в фокусе. Идущая сессия сюда не входит — её время
// консоль показывает сама, от started_at. Без аргумента — про себя; с id —
// про коллегу, это читает его профиль.
export function focusStatsQueryOptions(employeeId?: string) {
  const query = employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : ''
  return queryOptions({
    queryKey: soundscapeKeys.statsOf(employeeId ?? 'me'),
    queryFn: async (): Promise<FocusStats> => {
      return toFocusStats(unwrap<FocusStatsDto>(await apiGet(`/focus/stats${query}`)))
    },
  })
}

// Докуда сотрудник дослушал каждый режим. Список приходит разом: какой режим
// включат следующим, заранее неизвестно, а решение нужно в момент нажатия.
export const soundscapePositionsQueryOptions = queryOptions({
  queryKey: soundscapeKeys.positions(),
  queryFn: async (): Promise<SoundscapePosition[]> => {
    const list = unwrap<SoundscapePositionDto[]>(await apiGet('/focus/positions')) ?? []
    return list.map(toSoundscapePosition)
  },
})

// Точка возврата в режиме. Плеер шлёт её редко (по паузе, смене режима и раз в
// пару минут) — это отметка «докуда дослушал», а не поток телеметрии.
export function savePosition(slug: string, positionSeconds: number): Promise<unknown> {
  return apiPut(`/focus/soundscapes/${encodeURIComponent(slug)}/position`, {
    position_seconds: Math.max(0, Math.round(positionSeconds)),
  })
}

// URL плейлиста режима. Час передаём свой: Endel отдаёт разный микс на каждый
// час суток, а TZ знает только браузер.
export function streamUrl(slug: string, hour = new Date().getHours()): string {
  return `/api/focus/soundscapes/${encodeURIComponent(slug)}/stream.m3u8?hour=${hour}`
}
